import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Select, Button, Row, H5, Paragraph, TextField, Separator, colors } from '@fs/zion-ui'
import { css } from '@emotion/core'
import axios from '@fs/zion-axios'
import { saveAs } from 'file-saver'
import useNotifications from '../../../hooks/useNotifications'
import { NUMERIC_DATE_FORMAT, timestampToDateString, isDateBefore, isDateAfter } from '../../../util/DateUtil'

const startDateErrorStyle = css`
  color: ${colors.red60};
`

const REPORT_OPTIONS = [
  { label: 'Queue Statistics Summary', value: 'queue-statistics-summary' },
  { label: 'Filtered Before Queues', value: 'filtered-before-sent-to-queues' },
]

const BASE_REPORTS_PATH = '/service/dmc/reports'

function getDefaultDates() {
  const now = new Date()
  const firstOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1)
  const lastDayOfPrevMonth = new Date(firstOfCurrentMonth.getTime() - 1)
  const firstDayOfPrevMonth = new Date(lastDayOfPrevMonth.getFullYear(), lastDayOfPrevMonth.getMonth(), 1)
  return {
    start: timestampToDateString({ timestamp: firstDayOfPrevMonth, dateFormat: NUMERIC_DATE_FORMAT }),
    end: timestampToDateString({ timestamp: lastDayOfPrevMonth, dateFormat: NUMERIC_DATE_FORMAT }),
  }
}

const defaultDates = getDefaultDates()

const MAX_HISTORY_MONTHS = 13

export default function Reports() {
  const { t } = useTranslation()
  const { error } = useNotifications()

  const [selectedReport, setSelectedReport] = useState('queue-statistics-summary')
  const [startDate, setStartDate] = useState(defaultDates.start)
  const [endDate, setEndDate] = useState(defaultDates.end)
  const [downloading, setDownloading] = useState(false)

  const startDateMissing = !startDate
  const endDateMissing = !endDate
  const startDateTooFarBack = isDateBefore(startDate, { months: MAX_HISTORY_MONTHS })
  const endDateTooRecent = isDateAfter(endDate, { days: 1 })
  const endDateBeforeStart = isDateBefore(endDate, startDate)

  const shouldDisableDownload =
    downloading || startDateMissing || endDateMissing || startDateTooFarBack || endDateTooRecent || endDateBeforeStart

  const handleDownload = async () => {
    setDownloading(true)

    const startTimestamp = `${startDate}T00:00:00`
    const endTimestamp = `${endDate}T23:59:59.999`

    const body =
      selectedReport === 'queue-statistics-summary'
        ? { startDate: startTimestamp, endDate: endTimestamp, separateByTreeCategory: false }
        : { startDate: startTimestamp, endDate: endTimestamp }

    try {
      const response = await axios.post(`${BASE_REPORTS_PATH}/${selectedReport}`, body, {
        responseType: 'blob',
      })
      const startStr = startDate.replaceAll('-', '')
      const endStr = endDate.replaceAll('-', '')
      const filename =
        selectedReport === 'queue-statistics-summary'
          ? `Queue_Statistics_Summary_${startStr}_through_${endStr}.csv`
          : `Filtered_Before_Queues_${startStr}_through_${endStr}.csv`
      saveAs(response.data, filename)
    } catch (err) {
      error(t('reports.downloadError', 'Error downloading report'), err)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <>
      <H5>{t('reports.dmqReports', 'DMQ Reports')}</H5>
      <Paragraph>
        {t('reports.description', 'Choose report and set parameters for Data Management Queues report.')}
      </Paragraph>
      <Row>
        <Select
          label={`${t('reports.report', 'Report')}:`}
          hidePlaceholder
          value={selectedReport}
          options={REPORT_OPTIONS}
          onChange={(e) => setSelectedReport(e.target.value)}
        />
        <TextField
          label={`${t('reports.startDate', 'Start Date')}:`}
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        <TextField
          label={`${t('reports.endDate', 'End Date')}:`}
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
      </Row>
      {startDateMissing && (
        <Row>
          <div css={startDateErrorStyle}>{t('reports.startDateIncomplete', 'Start date is incomplete.')}</div>
        </Row>
      )}
      {!startDateMissing && startDateTooFarBack && (
        <Row>
          <div css={startDateErrorStyle}>
            {t('reports.startDateTooFarBack', 'Start date exceeds the 13-month limit.')}
          </div>
        </Row>
      )}
      {endDateMissing && (
        <Row>
          <div css={startDateErrorStyle}>{t('reports.endDateIncomplete', 'End date is incomplete.')}</div>
        </Row>
      )}
      {!endDateMissing && endDateTooRecent && (
        <Row>
          <div css={startDateErrorStyle}>{t('reports.endDateTooRecent', 'End date must be before today.')}</div>
        </Row>
      )}
      {!endDateMissing && !endDateTooRecent && endDateBeforeStart && (
        <Row>
          <div css={startDateErrorStyle}>
            {t('reports.endDateBeforeStart', 'End date must be on or after the start date.')}
          </div>
        </Row>
      )}
      <Row>
        <Button emphasis="high" disabled={shouldDisableDownload} onClick={handleDownload}>
          {t('reports.download', 'Download')}
        </Button>
      </Row>
      <Separator />
    </>
  )
}
